import { useCallback, useEffect, useState } from 'react'
import { API_EVENTS } from '@/constants'
import { maintenanceApi } from '@/services/maintenance'

const POLL_INTERVAL_MS = 30_000

export interface MaintenanceState {
  enabled: boolean
  message: string | null
}

/**
 * メンテナンス状態を監視するフック。
 * - 公開ステータス API を定期ポーリング（ログイン中ユーザーも一定時間内に検知して強制ログアウトできる）。
 * - axios インターセプターが 503/MAINTENANCE を捕捉した際の即時イベントも購読する（能動的な API 呼び出しで即検知）。
 */
export function useMaintenance(): MaintenanceState {
  const [state, setState] = useState<MaintenanceState>({ enabled: false, message: null })

  const refresh = useCallback(async () => {
    try {
      const { data } = await maintenanceApi.status()
      setState({ enabled: Boolean(data.enabled), message: data.message ?? null })
    } catch {
      /* 取得失敗時は状態を変えない（誤って sorry を表示しない）。 */
    }
  }, [])

  useEffect(() => {
    // 起動時に一度メンテ状態を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
    const timer = setInterval(() => {
      void refresh()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [refresh])

  useEffect(() => {
    const handler = () => {
      // インターセプター検知時はまず即座に enabled へ倒し、メッセージは裏で最新化する。
      setState((prev) => (prev.enabled ? prev : { ...prev, enabled: true }))
      void refresh()
    }
    window.addEventListener(API_EVENTS.MAINTENANCE, handler)
    return () => window.removeEventListener(API_EVENTS.MAINTENANCE, handler)
  }, [refresh])

  return state
}
